import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Send,
  Plus,
  Play,
  Pause,
  ShieldCheck,
  QrCode,
  SlidersHorizontal,
  RefreshCw,
  Zap,
} from "lucide-react";
import {
  listCampaigns,
  createCampaign,
  startCampaign,
  pauseCampaign,
  type CurrentUser,
  type Campaign,
} from "../../lib/api";

export function CampaignsView({ token, user, tenantId }: { token: string; user: CurrentUser; tenantId?: string }) {
  const queryClient = useQueryClient();
  const effectiveTenantId = user.role === "tenant" ? user.tenant_id : (tenantId && tenantId !== "all" ? tenantId : user.tenant_id);
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Wizard Form State
  const [campaignName, setCampaignName] = useState("");
  const [channelType, setChannelType] = useState<"qr_whatsapp" | "meta_official">("qr_whatsapp");
  const [customMessage, setCustomMessage] = useState("");
  const [pacingPerMinute, setPacingPerMinute] = useState(30);

  // Live Campaigns Query
  const campaignsQuery = useQuery({
    queryKey: ["campaigns", effectiveTenantId],
    queryFn: () => listCampaigns(token, effectiveTenantId),
    refetchInterval: 5_000,
  });

  const campaigns = campaignsQuery.data || [];

  // Create Campaign Mutation
  const createCampaignMutation = useMutation({
    mutationFn: () =>
      createCampaign(
        token,
        {
          name: campaignName.trim(),
          channel_type: channelType,
          message_content: {
            text: customMessage || "Hello! We have an exciting update for you.",
          },
          pacing_per_minute: pacingPerMinute,
        },
        user.tenant_id
      ),
    onSuccess: async (newCamp) => {
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      setIsWizardOpen(false);
      setCampaignName("");
      setCustomMessage("");
      if (newCamp?.id) {
        try {
          await startCampaign(token, newCamp.id);
          queryClient.invalidateQueries({ queryKey: ["campaigns"] });
        } catch (err: any) {
          console.error("Failed to auto-start campaign:", err);
        }
      }
    },
    onError: (err: any) => {
      alert(`Failed to create campaign: ${err.message || "Unknown error"}`);
    },
  });

  // Start Campaign Mutation
  const startMutation = useMutation({
    mutationFn: (campaignId: string) => startCampaign(token, campaignId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["campaigns"] }),
    onError: (err: any) => {
      alert(`Failed to start campaign: ${err.message || "Unknown error"}`);
    },
  });

  // Pause Campaign Mutation
  const pauseMutation = useMutation({
    mutationFn: (campaignId: string) => pauseCampaign(token, campaignId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["campaigns"] }),
    onError: (err: any) => {
      alert(`Failed to pause campaign: ${err.message || "Unknown error"}`);
    },
  });

  const filteredCampaigns = campaigns.filter((c) =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
            <Send className="w-6 h-6 text-emerald-600" />
            Campaign Broadcast Studio
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Create, schedule, and execute high-throughput broadcasts with anti-ban pacing.
          </p>
        </div>

        <button
          onClick={() => setIsWizardOpen(true)}
          className="btn-primary text-xs py-2 px-3.5 flex items-center gap-1.5 shadow-2xs"
        >
          <Plus className="w-3.5 h-3.5" />
          <span>New Broadcast Campaign</span>
        </button>
      </div>

      {/* Filter Toolbar */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {/* Search Box */}
          <div className="relative w-64">
            <input
              type="text"
              placeholder="Search campaigns..."
              className="modern-input w-full text-xs py-2"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <button className="filter-chip">
            <span>⊕ Channel Type</span>
          </button>

          <button className="filter-chip">
            <span>⊕ Campaign Status</span>
          </button>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => campaignsQuery.refetch()}
            className="btn-secondary text-xs py-2 px-3 flex items-center gap-1.5 shadow-2xs"
            title="Refresh database state"
          >
            <RefreshCw className={`w-3.5 h-3.5 text-slate-500 ${campaignsQuery.isFetching ? "animate-spin" : ""}`} />
            <span>Sync</span>
          </button>

          <button className="btn-secondary text-xs py-2 px-3 flex items-center gap-1.5 shadow-2xs">
            <SlidersHorizontal className="w-3.5 h-3.5 text-slate-500" />
            <span>View</span>
          </button>
        </div>
      </div>

      {/* Campaigns Grid / Table */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {campaignsQuery.isLoading ? (
          <div className="col-span-3 p-16 text-center text-slate-400 text-xs bg-white border border-slate-200 rounded-xl shadow-2xs">
            <div className="flex flex-col items-center justify-center gap-2">
              <RefreshCw className="w-6 h-6 text-emerald-600 animate-spin" />
              <span className="font-semibold text-slate-600">Loading broadcast campaigns...</span>
              <span className="text-[11px] text-slate-400">Fetching delivery queues and dispatch telemetry</span>
            </div>
          </div>
        ) : filteredCampaigns.length === 0 ? (
          <div className="col-span-3 p-12 text-center bg-white border border-slate-200 rounded-xl shadow-2xs space-y-3">
            <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center mx-auto text-emerald-600">
              <Send className="w-6 h-6" />
            </div>
            <h3 className="text-sm font-bold text-slate-900">No campaigns launched yet</h3>
            <p className="text-xs text-slate-500 max-w-md mx-auto">
              Broadcast personalized announcements, promotions, or notifications to your WhatsApp CRM audience.
            </p>
            <button
              onClick={() => setIsWizardOpen(true)}
              className="btn-primary text-xs py-2 px-3.5 mx-auto inline-flex items-center gap-1.5"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>Create Campaign</span>
            </button>
          </div>
        ) : (
          filteredCampaigns.map((campaign: Campaign) => {
            const isMeta = campaign.channel_type === "meta_official";
            const isRunning = campaign.status === "running";

            return (
              <div
                key={campaign.id}
                className="bg-white border border-slate-200 rounded-xl p-5 shadow-2xs flex flex-col justify-between hover:border-slate-300 transition-all space-y-4"
              >
                <div>
                  {/* Top Status & Title */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2.5">
                      <div className={`p-2 rounded-lg ${isMeta ? "bg-blue-50 text-blue-600 border border-blue-200" : "bg-emerald-50 text-emerald-600 border border-emerald-200"}`}>
                        {isMeta ? <ShieldCheck className="w-4 h-4" /> : <QrCode className="w-4 h-4" />}
                      </div>
                      <div>
                        <h3 className="text-sm font-bold text-slate-900">{campaign.name}</h3>
                        <p className="text-[11px] text-slate-400 font-mono mt-0.5">
                          {isMeta ? "Official Meta WABA" : "Multi-Device QR Node"}
                        </p>
                      </div>
                    </div>

                    <span
                      className={`status-pill ${
                        isRunning
                          ? "status-pill--connected"
                          : campaign.status === "paused"
                          ? "status-pill--pending"
                          : "bg-slate-100 text-slate-700 border border-slate-200"
                      }`}
                    >
                      {campaign.status}
                    </span>
                  </div>

                  {/* Message Content Preview */}
                  <div className="mt-3 p-3 rounded-lg bg-slate-50 border border-slate-200 text-xs space-y-2">
                    <p className="text-slate-600 line-clamp-2">
                      {campaign.message_content?.text || "Template Message"}
                    </p>
                    <div className="flex justify-between items-center text-[11px] text-slate-500 pt-2 border-t border-slate-200/60">
                      <span>Anti-Ban Pacing:</span>
                      <span className="font-mono font-bold text-emerald-700">
                        {campaign.pacing_per_minute} msgs/min
                      </span>
                    </div>
                  </div>
                </div>

                {/* Card Actions */}
                <div className="pt-3 border-t border-slate-100 flex items-center justify-between gap-2">
                  {isRunning ? (
                    <button
                      onClick={() => pauseMutation.mutate(campaign.id)}
                      disabled={pauseMutation.isPending}
                      className="btn-secondary text-xs py-1.5 px-3 flex items-center gap-1.5 flex-1 text-amber-600"
                    >
                      <Pause className="w-3.5 h-3.5" />
                      <span>Pause</span>
                    </button>
                  ) : (
                    <button
                      onClick={() => startMutation.mutate(campaign.id)}
                      disabled={startMutation.isPending}
                      className="btn-primary text-xs py-1.5 px-3 flex items-center gap-1.5 flex-1 shadow-2xs"
                    >
                      <Play className="w-3.5 h-3.5" />
                      <span>Start Broadcast</span>
                    </button>
                  )}

                  <span className="text-[11px] text-slate-400 font-mono">
                    {new Date(campaign.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* 4-Step Broadcast Campaign Wizard Modal */}
      {isWizardOpen && (
        <div className="modal-backdrop" onClick={() => setIsWizardOpen(false)}>
          <div className="modal-card max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between pb-3 border-b border-slate-200 mb-3">
              <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <Send className="w-4 h-4 text-emerald-600" />
                Launch WhatsApp Broadcast Campaign
              </h3>
              <button
                onClick={() => setIsWizardOpen(false)}
                className="text-slate-400 hover:text-slate-700 text-xs font-bold"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              {/* Step 1: Name */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Campaign Name *
                </label>
                <input
                  type="text"
                  placeholder="e.g. Festive Special Offer 2026"
                  value={campaignName}
                  onChange={(e) => setCampaignName(e.target.value)}
                  className="modern-input w-full text-xs"
                  autoFocus
                />
              </div>

              {/* Step 2: Channel Selection */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                  Sender Channel *
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setChannelType("qr_whatsapp")}
                    className={`p-3 rounded-lg border text-left transition-all ${
                      channelType === "qr_whatsapp"
                        ? "bg-emerald-50 border-emerald-500 text-emerald-900"
                        : "border-slate-200 text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    <div className="flex items-center gap-1.5 font-bold text-xs">
                      <QrCode className="w-3.5 h-3.5 text-emerald-600" />
                      Direct QR Node
                    </div>
                    <p className="text-[11px] text-slate-500 mt-1">Token-bucket anti-ban protection</p>
                  </button>

                  <button
                    type="button"
                    onClick={() => setChannelType("meta_official")}
                    className={`p-3 rounded-lg border text-left transition-all ${
                      channelType === "meta_official"
                        ? "bg-blue-50 border-blue-500 text-blue-900"
                        : "border-slate-200 text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    <div className="flex items-center gap-1.5 font-bold text-xs">
                      <ShieldCheck className="w-3.5 h-3.5 text-blue-600" />
                      Official Meta WABA
                    </div>
                    <p className="text-[11px] text-slate-500 mt-1">Pre-approved rich templates</p>
                  </button>
                </div>
              </div>

              {/* Step 3: Message Text */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-semibold text-slate-700">
                    Message Content *
                  </label>
                  <div className="flex items-center gap-1 text-[10px]">
                    <span className="text-slate-400">Insert:</span>
                    <button
                      type="button"
                      onClick={() => setCustomMessage((prev) => prev + " {{first_name}}")}
                      className="px-1.5 py-0.5 rounded bg-slate-100 hover:bg-slate-200 text-slate-700 font-mono"
                      title="Insert dynamic first name variable"
                    >
                      {`{{first_name}}`}
                    </button>
                    <button
                      type="button"
                      onClick={() => setCustomMessage((prev) => prev + " {Hello|Hi|Hey}")}
                      className="px-1.5 py-0.5 rounded bg-emerald-50 hover:bg-emerald-100 text-emerald-700 font-mono font-medium"
                      title="Insert Spintax variation"
                    >
                      {"{Hello|Hi}"}
                    </button>
                  </div>
                </div>
                <textarea
                  placeholder="{Hello|Hi} {{first_name}}! Check out our latest offer. Reply STOP to opt out."
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  className="modern-input w-full text-xs h-24"
                />
                <div className="mt-1.5 p-2 rounded-lg bg-emerald-50/70 border border-emerald-100 text-[11px] text-emerald-800 flex items-start gap-1.5">
                  <ShieldCheck className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" />
                  <div>
                    <strong>Anti-Ban Spintax:</strong> Use <code className="bg-emerald-100/80 px-1 rounded font-mono text-[10px] text-emerald-900">{`{Option 1|Option 2|Option 3}`}</code> to randomize text for each contact, eliminating duplicate spam fingerprints. Typing simulation and cooldown pauses run automatically.
                  </div>
                </div>
              </div>

              {/* Step 4: Anti-Ban Pacing Slider */}
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-2">
                <div className="flex justify-between items-center text-xs">
                  <span className="font-semibold text-slate-700 flex items-center gap-1">
                    <Zap className="w-3.5 h-3.5 text-amber-500" /> Anti-Ban Dispatch Pacing:
                  </span>
                  <span className="font-mono font-bold text-emerald-700">{pacingPerMinute} msgs/min</span>
                </div>
                <input
                  type="range"
                  min="5"
                  max="120"
                  value={pacingPerMinute}
                  onChange={(e) => setPacingPerMinute(Number(e.target.value))}
                  className="w-full accent-emerald-600"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <button
                  className="btn-secondary text-xs"
                  onClick={() => setIsWizardOpen(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn-primary text-xs"
                  onClick={() => createCampaignMutation.mutate()}
                  disabled={!campaignName.trim() || !customMessage.trim() || createCampaignMutation.isPending}
                >
                  {createCampaignMutation.isPending ? "Creating..." : "Schedule & Launch"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
